interface QueryState {
    isLoading: boolean
    error: unknown
}

interface MergedQueryState {
    isLoading: boolean
    error: unknown
}

/**
 * Merges multiple query states into a single loading/error pair.
 * - `isLoading` is true if any query is still loading
 * - `error` is the first non-null error from any query
 *
 * Both flags may be true simultaneously (one query errored, another still loading).
 * Callers (PageLayout) give loading priority over error display.
 */
export function mergeQueryState(queries: QueryState[]): MergedQueryState {
    if (queries.length === 0) {
        return {isLoading: false, error: null}
    }

    const isLoading = queries.some(q => q.isLoading)
    const error = queries.find(q => q.error != null)?.error ?? null

    return {isLoading, error}
}

/**
 * Computes the error prop for detail/edit pages that load a single entity.
 * Returns the fetch error if present, or a "not found" error if loading finished
 * but no data was returned (e.g., deleted entity, invalid ID).
 */
export function queryError(isLoading: boolean, data: unknown, error: unknown): unknown {
    if (error) return error
    if (!isLoading && !data) return new Error('Not found')
    return undefined
}
